import {
  createEffect,
  createResource,
  createSignal,
  For,
  Match,
  Show,
  Suspense,
  Switch,
  type Component,
} from "solid-js";
import Drawer from "corvu/drawer";
import { IconCTA } from "@core/components/cta/IconCTA";
import { IoClose } from "solid-icons/io";
import { useWire } from "@core/wire";
import { CollectionKind } from "@core/domains/collections/collections.types";
import { APIError, type curator, getRequestClient } from "@repo/client";
import { parsePromptTextMentions } from "@core/lib/parsePromptTextMentions";
import { PromptAssetCTA, PromptCollectionCTA } from "../modals/ImprovePromptModal";
import {
  TbArrowUpRight,
  TbBook,
  TbCheck,
  TbDots,
  TbLink,
  TbPencil,
  TbShare3,
  TbTrash,
  TbUsersGroup,
  TbStar,
  TbStarFilled,
} from "solid-icons/tb";
import { TbCopy } from "solid-icons/tb";
import { StDropdown } from "@core/components/_original/StDropdown";
import { StIcon } from "@core/components/icons/StIcon";
import { usePromptContext } from "@core/domains/chat/prompt/PromptContext";
import { SaveToPromptLibraryModal } from "../modals/SaveToPromptLibraryModal";
import Dialog from "corvu/dialog";
import { PrimaryCTA } from "@core/components/cta/PrimaryCTA";
import { CollectionBreadcrumbs } from "@core/components/CollectionBreadcrumbs";
import { BsPersonFillLock } from "solid-icons/bs";
import { useUIState } from "@core/ui/UIState";
import { SecondaryCTA } from "@core/components/cta/SecondaryCTA";
import { InfiniteScroll } from "@core/components/_original/InfiniteScroll";
import { createStore } from "solid-js/store";
import { Spinner } from "@core/components/_original/Spinner";
import { QuestionLibraryQuestions } from "@core/domains/chat/prompt/components/ChatQuestionLibrary";
import { twMerge } from "tailwind-merge";

export const PromptLibraryDrawer = () => {
  const ui = useUIState();
  const [open, setOpen] = ui.promptLibraryDrawer;

  return (
    <Drawer open={open()} side="right" onOpenChange={setOpen}>
      {(props) => (
        <Drawer.Portal>
          <Drawer.Overlay
            class="fixed inset-0 z-max data-[transitioning]:transition-colors data-[transitioning]:duration-500 data-[transitioning]:ease-[cubic-bezier(0.32,0.72,0,1)]"
            style={{
              "background-color": `rgb(0 0 0 / ${0.5 * props.openPercentage})`,
            }}
          />
          <PromptLibraryContent {...props} />
        </Drawer.Portal>
      )}
    </Drawer>
  );
};

const PromptLibraryContent: Component<{ open: boolean; setOpen: (open: boolean) => void }> = (props) => {
  const wire = useWire();
  const client = getRequestClient(wire.services.auth.token);
  const { typePrompt } = usePromptContext();
  const personalCollection = () => wire.services.collections.getRootCollectionByKind(CollectionKind.Personal);

  const [updateModal, setUpdateModal] = createSignal<undefined | curator.StoredPromptSnapshot>(undefined);
  const [saveModal, setSaveModal] = createSignal(false);
  const [deleteModal, setDeleteModal] = createSignal<curator.StoredPromptSnapshot | undefined>(undefined);
  const [activeTab, setActiveTab] = createSignal<"all" | "favorites" | "personal" | "shared">("all");

  const isShared = (collectionID: string) => {
    return collectionID !== personalCollection()?.id;
  };

  const [data, setData] = createStore({
    data: [] as curator.StoredPromptSnapshot[],
    loading: false,
    canFetchMore: true,
    error: undefined as string | undefined,
  });

  const fetchPrompts = async (next?: boolean) => {
    setData({
      loading: true,
      error: undefined,
    });

    try {
      const res = await client.controlplane.ListStoredPrompts({
        limit: 100,
        offset: next ? data.data.length : 0,
        searchFilter: "",
      });

      const newData = next ? [...data.data, ...res.data.Prompts] : res.data.Prompts;
      setData({
        data: newData,
        canFetchMore: newData.length < res.data.TotalCount,
        error: undefined,
        loading: false,
      });
    } catch {
      setData({
        loading: false,
        error: "Something went wrong",
      });
    }
  };

  createEffect(() => {
    if (wire.services.auth.isReady()) {
      fetchPrompts();
    }
  });

  const [expanded, setExpanded] = createSignal<Record<string, boolean>>({});
  const [filter, setFilter] = createSignal("");

  const toggleFavorite = async (prompt: curator.StoredPromptSnapshot) => {
    try {
      await client.controlplane.UpdateStoredPrompt(prompt.id, {
        id: prompt.id,
        name: prompt.name,
        description: prompt.description,
        prompt: prompt.prompt,
        collectionId: prompt.collectionID,
        favorite: !prompt.favorite,
      });
      fetchPrompts();
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  const filteredPrompts = () => {
    let prompts = data.data;
    if (activeTab() === "favorites") {
      prompts = prompts.filter((p) => p.favorite);
    } else if (activeTab() === "personal") {
      prompts = prompts.filter((p) => p.collectionID === personalCollection()?.id);
    } else if (activeTab() === "shared") {
      prompts = prompts.filter((p) => p.collectionID !== personalCollection()?.id);
    }
    return prompts;
  };

  return (
    <>
      <Drawer.Content class="fixed inset-y-0 right-0 z-max flex h-full w-full sm:max-w-[50rem] flex-col p-4 data-[transitioning]:transition-transform data-[transitioning]:duration-500 data-[transitioning]:ease-[cubic-bezier(0.32,0.72,0,1)] bg-background">
        <div class="flex justify-between items-center mb-4">
          <div>
            <Drawer.Label class="text-xl font-bold dark:text-white">Prompt Library</Drawer.Label>

            <Drawer.Description class="text-gray-600 dark:text-gray-300">
              Browse and select prompts from your personal library or shared collections
            </Drawer.Description>
          </div>
          <Drawer.Close
            as={IconCTA}
            class="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 w-auto"
            data-test-id="right-drawer-close"
            accessibleLabel="Close the overlay."
            modifiers={["small"]}
            icon={IoClose}
          />
        </div>
        <div class="flex justify-end items-center mb-6">
          <SaveToPromptLibraryModal
            onSave={() => {
              setFilter("");
              fetchPrompts();
            }}
            trigger={
              <Dialog.Trigger
                as={PrimaryCTA}
                label="Create prompt"
                data-test-id="create-prompt-button"
                class="w-auto h-auto min-w-0 min-h-10"
              />
            }
            initialPrompt={""}
            open={saveModal()}
            setOpen={setSaveModal}
          />
        </div>

        <div class="flex gap-2 mb-4">
          <button
            class={twMerge(
              "px-2 py-0.5 text-center min-w-0 min-h-0 rounded text-xs font-medium transition-colors w-auto",
              activeTab() === "all"
                ? "bg-primary text-on-primary"
                : "bg-surface text-on-surface hover:bg-surface-decoration",
            )}
            type="button"
            onClick={() => setActiveTab("all")}
          >
            <TbCheck class="inline-block align-middle mr-1 -mt-0.5" size={16} />
            All
          </button>
          <button
            class={twMerge(
              "px-2 py-0.5 text-center min-w-0 min-h-0 rounded text-xs font-medium transition-colors w-auto",
              activeTab() === "favorites"
                ? "bg-primary text-on-primary"
                : "bg-surface text-on-surface hover:bg-surface-decoration",
            )}
            type="button"
            onClick={() => setActiveTab("favorites")}
          >
            <TbStar class="inline-block align-middle mr-1 -mt-0.5" size={16} />
            Favorites
          </button>
          <button
            class={twMerge(
              "px-2 py-0.5 text-center min-w-0 min-h-0 rounded text-xs font-medium transition-colors w-auto",
              activeTab() === "personal"
                ? "bg-primary text-on-primary"
                : "bg-surface text-on-surface hover:bg-surface-decoration",
            )}
            type="button"
            onClick={() => setActiveTab("personal")}
          >
            <BsPersonFillLock class="inline-block align-middle mr-1 -mt-0.5" size={16} />
            Personal
          </button>
          <button
            class={twMerge(
              "px-2 py-0.5 text-center min-w-0 min-h-0 rounded text-xs font-medium transition-colors w-auto",
              activeTab() === "shared"
                ? "bg-primary text-on-primary"
                : "bg-surface text-on-surface hover:bg-surface-decoration",
            )}
            type="button"
            onClick={() => setActiveTab("shared")}
          >
            <TbUsersGroup class="inline-block align-middle mr-1 -mt-0.5" size={16} />
            Shared
          </button>
        </div>

        <Show when={data.data.length}>
          <input
            type="text"
            class="bg-surface px-3 py-2 w-full rounded-lg outline-none focus:outline-none mb-4 text-on-surface"
            placeholder="Search prompts"
            onInput={(e) => {
              setFilter(e.currentTarget.value);
            }}
          />
        </Show>

        <InfiniteScroll
          shouldFetchMore={data.canFetchMore}
          isFetching={data.loading}
          fetchNext={() => fetchPrompts(true)}
          class="text-on-background flex-auto overflow-y-auto pr-3 flex flex-col gap-6"
        >
          <Suspense fallback={<div>Loading...</div>}>
            <For
              each={filteredPrompts()
                .slice()
                .sort((a, b) => Number(b.favorite) - Number(a.favorite))
                .filter(
                  (p) =>
                    p.name.toLowerCase().includes(filter().toLowerCase()) ||
                    p.description.toLowerCase().includes(filter().toLowerCase()) ||
                    p.prompt.toLowerCase().includes(filter().toLowerCase()),
                )}
            >
              {(prompt) => (
                <div
                  data-corvu-no-drag
                  class="relative grid gap-3 grid-cols-[auto_1fr_auto] text-on-surface border border-surface border-solid rounded-lg p-2"
                >
                  <div class="rounded-full h-10 w-10 flex items-center justify-center bg-surface">
                    <StIcon
                      icon={prompt.collectionID !== personalCollection()?.id ? TbUsersGroup : BsPersonFillLock}
                      class="mt-1"
                      size={18}
                    />
                  </div>
                  <div class="flex flex-col flex-auto [&_a]:min-h-0">
                    <div class="flex items-center justify-between gap-2">
                      <h4 class="type-subtitle font-medium -mb-1 flex-auto">{prompt.name}</h4>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(prompt);
                        }}
                        class="text-yellow-500 min-w-0 min-h-0 hover:text-yellow-600 transition-colors mt-1 w-5"
                        aria-label={prompt.favorite ? "Remove from favorites" : "Add to favorites"}
                      >
                        <StIcon icon={prompt.favorite ? TbStarFilled : TbStar} size={20} />
                      </button>
                    </div>
                    <Show when={prompt.collectionID !== personalCollection()?.id}>
                      <div class="mb-2">
                        <CollectionBreadcrumbs
                          inline
                          max={3}
                          breadcrumbs={wire.services.collections.getBreadcrumbs(prompt.collectionID) || []}
                        />
                      </div>
                    </Show>
                    <p class="type-body-sm text-on-background-variant font-thin mb-3">{prompt.description}</p>

                    <button
                      type="button"
                      class="h-auto min-h-0"
                      onClick={() => {
                        setExpanded((e) => ({ ...e, [prompt.id]: !e[prompt.id] }));
                      }}
                    >
                      <p
                        classList={{
                          "line-clamp-3": !expanded()[prompt.id],
                        }}
                        class="leading-relaxed whitespace-pre-wrap type-body-sm px-2 py-1 rounded bg-surface"
                      >
                        <For each={parsePromptTextMentions(prompt.prompt?.replaceAll("\n\n", "\n") || "")}>
                          {(segment) => (
                            <Switch>
                              <Match when={segment.type === "text" && segment.text}>{(s) => <span>{s()}</span>}</Match>
                              <Match when={segment.type === "collection" && segment}>
                                {(s) => <PromptCollectionCTA id={s().id} name={s().name} />}
                              </Match>
                              <Match when={segment.type === "asset" && segment}>
                                {(s) => <PromptAssetCTA id={s().id} name={s().name} />}
                              </Match>
                            </Switch>
                          )}
                        </For>
                      </p>
                    </button>
                  </div>

                  <div class="flex flex-col justify-between">
                    <StDropdown
                      theme="default"
                      content={{
                        style: {
                          "z-index": "99999",
                        },
                      }}
                      items={[
                        {
                          kind: "item",
                          content: prompt.favorite ? "Remove from favorites" : "Add to favorites",
                          icon: prompt.favorite ? TbStarFilled : TbStar,
                          props: {
                            onSelect: () => {
                              toggleFavorite(prompt);
                            },
                          },
                        },
                        isShared(prompt.collectionID)
                          ? {
                              kind: "item",
                              content: "Copy share link",
                              icon: TbLink,
                              props: {
                                onSelect: () => {
                                  navigator.clipboard.writeText(
                                    `https://${APP_DOMAIN}?prompt=${encodeURIComponent(prompt.prompt)}`,
                                  );
                                },
                              },
                            }
                          : {
                              kind: "menu",
                              trigger: {
                                content: "Share prompt",
                                icon: TbShare3,
                              },
                              items: [
                                {
                                  kind: "item",
                                  content: "Copy share link",
                                  icon: TbLink,
                                  props: {
                                    onSelect: () => {
                                      navigator.clipboard.writeText(
                                        `https://${APP_DOMAIN}?prompt=${encodeURIComponent(prompt.prompt)}`,
                                      );
                                    },
                                  },
                                },
                                {
                                  kind: "item",
                                  content: "Move to shared Collection",
                                  icon: TbUsersGroup,
                                  props: {
                                    onSelect: () => {
                                      setUpdateModal(prompt);
                                    },
                                  },
                                },
                              ],
                            },
                        {
                          kind: "item",
                          content: "Delete prompt",
                          icon: TbTrash,
                          props: {
                            onSelect: () => {
                              setDeleteModal(prompt);
                            },
                          },
                        },
                        {
                          kind: "item",
                          content: "Edit prompt",
                          icon: TbPencil,
                          props: {
                            onSelect: () => {
                              setUpdateModal(prompt);
                            },
                          },
                        },
                        {
                          kind: "item",
                          content: "Copy prompt",
                          icon: TbCopy,
                          props: {
                            onSelect: () => {
                              navigator.clipboard.writeText(prompt.prompt);
                            },
                          },
                        },
                        {
                          kind: "item",
                          content: "Use prompt",
                          icon: TbCheck,
                          props: {
                            onSelect: () => {
                              typePrompt(prompt.prompt);
                              props.setOpen(false);
                            },
                          },
                        },
                      ]}
                      trigger={{
                        class:
                          "hover:bg-surface transition-colors flex items-center justify-center rounded-full min-w-7 min-h-7 w-7 h-7",
                        onClick: (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        },
                      }}
                    >
                      <StIcon icon={TbDots} size={20} stroke-width={3} />
                    </StDropdown>

                    <button
                      onClick={() => {
                        typePrompt(prompt.prompt);
                        props.setOpen(false);
                      }}
                      type="button"
                      class="rounded bg-primary text-on-primary flex items-center justify-center min-w-8 min-h-8 w-8 h-8 mt-2"
                    >
                      <StIcon icon={TbArrowUpRight} size={16} stroke-width={3} />
                    </button>
                  </div>
                </div>
              )}
            </For>

            <Show when={data.loading}>
              <div class="flex justify-center items-center h-full">
                <Spinner />
              </div>
            </Show>

            <h4 class="type-title-sm">Recommended by Storytell</h4>

            <For each={QuestionLibraryQuestions}>
              {(question) => (
                <div
                  data-corvu-no-drag
                  class="grid gap-3 grid-cols-[auto_1fr_auto] text-on-surface border border-surface border-solid rounded-lg p-2"
                >
                  <div class="rounded-full h-10 w-10 flex items-center justify-center bg-surface">
                    <StIcon icon={TbBook} class="mt-1" size={18} />
                  </div>
                  <div class="flex flex-col flex-auto [&_a]:min-h-0">
                    <h4 class="type-subtitle font-medium -mb-1">{question.label}</h4>
                    <p class="type-body-sm text-on-background-variant font-thin mb-3">Created by Storytell</p>

                    <button
                      type="button"
                      class="h-auto min-h-0"
                      onClick={() => {
                        setExpanded((e) => ({ ...e, [question.prompt?.id || ""]: !e[question.prompt?.id || ""] }));
                      }}
                    >
                      <p
                        classList={{
                          "line-clamp-4": !expanded()[question.prompt?.id || ""],
                        }}
                        class="leading-relaxed whitespace-pre-wrap type-body-sm px-2 py-1 rounded bg-surface"
                      >
                        <For each={parsePromptTextMentions(question.prompt?.prompt || "")}>
                          {(segment) => (
                            <Switch>
                              <Match when={segment.type === "text" && segment.text}>{(s) => <span>{s()}</span>}</Match>
                              <Match when={segment.type === "collection" && segment}>
                                {(s) => <PromptCollectionCTA id={s().id} name={s().name} />}
                              </Match>
                              <Match when={segment.type === "asset" && segment}>
                                {(s) => <PromptAssetCTA id={s().id} name={s().name} />}
                              </Match>
                            </Switch>
                          )}
                        </For>
                      </p>
                    </button>
                  </div>

                  <div class="flex flex-col justify-end">
                    <button
                      onClick={() => {
                        typePrompt(question.prompt?.prompt || "");
                        props.setOpen(false);
                      }}
                      type="button"
                      class="rounded bg-primary text-on-primary flex items-center justify-center min-w-8 min-h-8 w-8 h-8 mt-2"
                    >
                      <StIcon icon={TbArrowUpRight} size={16} stroke-width={3} />
                    </button>
                  </div>
                </div>
              )}
            </For>
          </Suspense>
        </InfiniteScroll>

        <DeletePromptModal
          open={deleteModal()}
          setOpen={(val) => setDeleteModal(undefined)}
          onDelete={() => fetchPrompts()}
        />
        <SaveToPromptLibraryModal
          onSave={() => {
            setFilter("");
            fetchPrompts();
          }}
          open={!!updateModal()}
          setOpen={(val) => setUpdateModal(undefined)}
          initialPrompt={updateModal()?.prompt || ""}
          existingPrompt={updateModal()}
        />
      </Drawer.Content>
    </>
  );
};

const DeletePromptModal: Component<{
  open: curator.StoredPromptSnapshot | undefined;
  setOpen: (open: boolean) => void;
  onDelete: () => void;
}> = (props) => {
  const wire = useWire();
  const client = getRequestClient(wire.services.auth.token);
  const personalCollection = () => wire.services.collections.getRootCollectionByKind(CollectionKind.Personal);
  const isShared = (collectionID: string) => {
    return collectionID !== personalCollection()?.id;
  };

  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<string | undefined>();

  const onDelete = async () => {
    setLoading(true);
    const promptId = props.open?.id;
    if (!promptId) return;

    try {
      await client.controlplane.DeleteStoredPrompts({
        storedPromptIds: [promptId],
      });
      props.setOpen(false);
      props.onDelete();
    } catch (error) {
      if (error instanceof APIError) setError(error.message);
      else setError("Something went wrong");
    }
    setLoading(false);
  };

  return (
    <Dialog open={!!props.open} onOpenChange={props.setOpen} trapFocus={false}>
      <Dialog.Portal>
        <Dialog.Overlay class="fixed inset-0 z-max bg-black/50 data-[open]:animate-fade-in data-[closed]:animate-fade-out transition-opacity" />
        <Dialog.Content
          class={`text-on-background fixed left-1/2 top-1/2 z-max -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background px-6 py-5 w-full max-w-[50rem]
          data-[open]:animate-dialog-open data-[closed]:animate-dialog-close transition-opacity origin-top`}
        >
          <Dialog.Label class="type-title-sm mb-6">Delete prompt</Dialog.Label>

          <p class="type-body-sm text-on-background">Are you sure you want to delete this prompt?</p>
          <Show when={isShared(props.open?.collectionID || "")}>
            <p class="type-body-sm text-on-background">
              This prompt is part of a shared Collection. Deleting it will remove it for all users with access to the
              Collection.
            </p>
          </Show>

          <Show when={error()}>
            <p class="type-body-sm text-danger">{error()}</p>
          </Show>

          <div class="flex justify-end gap-2 mt-4">
            <SecondaryCTA
              data-test-id="delete-prompt-cancel"
              label="Cancel"
              accessibleSuffix=""
              onClick={() => props.setOpen(false)}
            />
            <PrimaryCTA
              loading={loading()}
              data-test-id="delete-prompt-delete"
              label="Delete"
              accessibleSuffix=" prompt"
              onClick={onDelete}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
